import { useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { ChevronLeft, Download, Calendar } from 'lucide-react';
import { NeuCard } from '@/components/ui/neu-card';
import { GaugeChart } from '@/components/domain/gauge-chart';
import { LazyPieChartWithSkeleton } from '@/components/charts/lazy-recharts';
import type { MonthDashboardData } from '@/api/plan-matriz';
import { cn } from '@/lib/utils';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

interface StatusEntry {
  name: string;
  value: number;
  color: string;
}

interface MonthDashboardCardProps {
  data: MonthDashboardData;
  /** Whether this card is the "hero" card for the current month */
  hero?: boolean;
  /** Animation delay index */
  index?: number;
  className?: string;
}

/**
 * Formats a number with dot separators (Chilean locale).
 * e.g., 1234.56 -> "1.234,56"
 */
function formatHH(value: number): string {
  return value.toLocaleString('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
}

export function MonthDashboardCard({ data, hero = false, index = 0, className }: MonthDashboardCardProps) {
  const navigate = useNavigate();
  const monthName = MONTH_NAMES[data.month - 1] ?? `Mes ${data.month}`;
  const shortYear = String(data.year).slice(2);

  const statusData: StatusEntry[] = ([
    { name: 'Pendientes' as const, value: data.pendientes, color: '#ff9f0a' },
    { name: 'Cerrados' as const, value: data.cerrados, color: '#30d158' },
    { name: 'No Ejecutados' as const, value: data.noEjecutados, color: '#ff453a' },
    { name: 'En Ejecucion' as const, value: data.enEjecucion, color: '#0071e3' },
  ] satisfies StatusEntry[]).filter((s) => s.value > 0);

  const handleCalendarView = () => {
    void navigate(`/plan-matriz/mensual?year=${data.year}&month=${data.month}`);
  };

  const gaugeSize = hero ? 180 : 140;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <NeuCard className={cn('flex flex-col', hero && 'ring-2 ring-sime-accent/20', className)}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className={cn(
            'font-semibold text-sime-text-primary',
            hero ? 'text-xl' : 'text-base',
          )}>
            {monthName} {`'${shortYear}`}
          </h3>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => { void navigate(-1); }}
              className="p-2 rounded-lg text-sime-text-secondary hover:text-sime-text-primary hover:bg-sime-surface-raised transition-colors"
              aria-label="Volver"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="p-2 rounded-lg text-sime-text-secondary hover:text-sime-text-primary hover:bg-sime-surface-raised transition-colors"
              aria-label="Descargar"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleCalendarView}
              className="p-2 rounded-lg text-sime-text-secondary hover:text-sime-text-primary hover:bg-sime-surface-raised transition-colors"
              aria-label="Ver calendario"
            >
              <Calendar className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Gauge + HH */}
        <div className="flex flex-col items-center mb-4">
          <GaugeChart
            value={data.avancePercent}
            size={gaugeSize}
            label="Avance"
          />
          <div className="mt-2 text-center">
            <p className={cn(
              'font-bold text-sime-text-primary',
              hero ? 'text-3xl' : 'text-2xl',
            )}>
              {formatHH(data.totalHH)}
            </p>
            <p className="text-xs text-sime-text-tertiary">HH</p>
          </div>
        </div>

        {/* Donut Chart */}
        {data.totalTickets > 0 ? (
          <div className="flex items-center gap-4">
            <div className={cn('shrink-0', hero ? 'w-24 h-24' : 'w-20 h-20')}>
              <LazyPieChartWithSkeleton data={statusData} hero={hero} />
            </div>
            <div className="flex flex-col gap-1.5 text-xs min-w-0">
              {statusData.map((entry) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sime-text-secondary truncate">
                    {entry.name}
                  </span>
                  <span className="font-medium text-sime-text-primary ml-auto">
                    {entry.value}
                  </span>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-1 border-t border-sime-border-subtle">
                <span className="text-sime-text-tertiary">Total</span>
                <span className="font-semibold text-sime-text-primary ml-auto">
                  {data.totalTickets}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-sm text-sime-text-tertiary py-4">
            Sin tickets en este periodo
          </p>
        )}
      </NeuCard>
    </motion.div>
  );
}
